"use client";

import { motion, AnimatePresence } from "motion/react";
import { usePathname, useRouter } from "next/navigation";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

type CurtainState = { color: string; phase: "in" | "out" } | null;

const CurtainContext = createContext<(href: string, color?: string) => void>(
  () => {},
);

/** Navigate to an internal route behind a colored curtain sweep. */
export const usePageCurtain = () => useContext(CurtainContext);

export function PageCurtainProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const [state, setState] = useState<CurtainState>(null);
  const [mounted, setMounted] = useState(false);
  const target = useRef<string | null>(null);

  useEffect(() => setMounted(true), []);

  const navigate = useCallback(
    (href: string, color: string = "#264653") => {
      if (href === pathname) return;
      target.current = href;
      setState({ color, phase: "in" });
    },
    [pathname],
  );

  // Once the new route is active, hold a beat for paint, then reveal.
  useEffect(() => {
    if (state?.phase === "in" && target.current && pathname === target.current) {
      const t = setTimeout(
        () => setState((s) => (s ? { ...s, phase: "out" } : s)),
        180,
      );
      return () => clearTimeout(t);
    }
  }, [pathname, state]);

  return (
    <CurtainContext.Provider value={navigate}>
      {children}
      {mounted &&
        createPortal(
          <AnimatePresence>
            {state && (
              <motion.div
                key="page-curtain"
                initial={{ y: "100%" }}
                animate={{ y: state.phase === "in" ? "0%" : "-100%" }}
                transition={{ duration: 0.5, ease: [0.76, 0, 0.24, 1] }}
                onAnimationComplete={() => {
                  if (state.phase === "in" && target.current) {
                    router.push(target.current);
                  } else if (state.phase === "out") {
                    setState(null);
                    target.current = null;
                  }
                }}
                className="fixed inset-0 z-[9998] flex items-center justify-center shadow-[0_-30px_60px_rgba(38,70,83,0.18)]"
                style={{ backgroundColor: state.color }}
              >
                <motion.span
                  initial={{ opacity: 0, scale: 0.6 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: 0.25 }}
                  className="flex items-baseline gap-2"
                  aria-hidden
                >
                  {["#FDF9F5", "#FDF9F5", "#FDF9F5"].map((c, i) => (
                    <motion.span
                      key={i}
                      className="size-2.5 rounded-full"
                      style={{ backgroundColor: c }}
                      animate={{ opacity: [0.4, 1, 0.4] }}
                      transition={{
                        duration: 0.9,
                        delay: i * 0.15,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    />
                  ))}
                </motion.span>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body,
        )}
    </CurtainContext.Provider>
  );
}
